import { Fragment, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App.tsx';
import Layout from './components/Layout.tsx';
import { BrowserRouter as Router } from 'react-router-dom';
import { config, isDevelopment, isProduction } from './config.ts';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { QueryClient, QueryCache, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@acast-tech/auth-client';
import { authClient } from './auth';
import { CACHE_TIMES } from '@/config/cache';
import { ApiError } from '@/services/queryUtils';
import Hotjar from '@hotjar/browser';
import * as Sentry from '@sentry/react';
import { CombinedProviders } from '@/context';
import { EchoProvider } from '@/components/echo/primitives';

if (isProduction) {
  const { siteId, hotjarVersion } = config.hotjarConfig;
  Hotjar.init(siteId, hotjarVersion);
}

if (isDevelopment || isProduction) {
  Sentry.init({
    dsn: config.sentryConfig.dsn,
    sendDefaultPii: config.sentryConfig.sendDefaultPii,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: isProduction ? 1.0 : 0.1,
    replaysOnErrorSampleRate: isProduction ? 1.0 : 0.1,
  });
}

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: config.launchDarklyClientId,
  });

  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (error instanceof ApiError && error.response.status === 401) {
          authClient.logout();
          window.location.href = '/';
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: CACHE_TIMES.MEDIUM,
        gcTime: CACHE_TIMES.LONG,
        retry: (failureCount, error) => {
          const status = (error as ApiError).response?.status;
          if (status === 401 || status === 403) return false;
          return failureCount < 2;
        },
        throwOnError: (error) => {
          const status = (error as ApiError).response?.status;
          return status === 403;
        },
        meta: {
          respectCache: true,
        },
      },
    },
  });

  // We only want to use StrictMode in local development
  const StrictModeWrapper = !isProduction && !isDevelopment ? StrictMode : Fragment;

  createRoot(document.getElementById('root')!).render(
    <StrictModeWrapper>
      <AuthProvider client={authClient}>
        <QueryClientProvider client={queryClient}>
          <CombinedProviders>
            <Router>
              <LDProvider>
                <EchoProvider>
                  <Layout>
                    <App />
                  </Layout>
                </EchoProvider>
              </LDProvider>
            </Router>
          </CombinedProviders>
        </QueryClientProvider>
      </AuthProvider>
    </StrictModeWrapper>
  );
})();
